import React, { useState } from 'react';
import { RotateCcw, RefreshCw, Info, Users, Loader2, Lock, SlidersHorizontal, History } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmployeePicker } from '@/components/performance/feedback/EmployeePicker';
import { reviewService, type ReviewSessionData } from '@/services/api/reviewService';
import { calibrationRemediationService } from '@/services/api/calibrationRemediationService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import toast from 'react-hot-toast';

/**
 * Structural shape of the employee objects emitted by EmployeePicker's
 * `onSelectionChange`. The picker declares this type locally (not exported),
 * so we mirror the fields we consume here. `id` is the personnel number
 * (employee_id) — exactly what `getUserReviewCycle(userId)` expects.
 */
interface PickedEmployee {
  id: string;
  name: string;
  role: string;
  department: string;
}

// Friendly labels for the review_type codes the backend returns.
const REVIEW_TYPE_LABELS: Record<string, string> = {
  'mid-year': 'Mid-Year Check-in',
  midyear: 'Mid-Year Check-in',
  'year-end': 'Year-End Check-in',
  yearend: 'Year-End Check-in',
  'q1-checkin': 'Q1 Check-in',
  q1_checkin: 'Q1 Check-in',
  'q3-checkin': 'Q3 Check-in',
  q3_checkin: 'Q3 Check-in',
  probation: 'Probation Check-in',
  adhoc: 'Ad-hoc Check-in',
  performance_improvement: 'Performance Improvement',
};

const formatReviewType = (type: string): string =>
  REVIEW_TYPE_LABELS[type] ||
  type
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Calibration phase → friendly label for the "reopen locked" note.
const CAL_STATUS_LABELS: Record<string, string> = {
  'pre-calibration': 'in pre-calibration',
  'report-generation': 'generating report',
  'in-progress': 'in progress',
  completed: 'completed',
};
const formatCalStatus = (s?: string | null): string =>
  s ? CAL_STATUS_LABELS[s] || s.replace(/[-_]/g, ' ') : 'concluded';

// Status → Badge variant + colour. Matches the muted palette used elsewhere
// in the admin surface (emerald = done, blue = active, amber = scheduled).
const getStatusBadge = (status: string): { label: string; className: string } => {
  switch (status) {
    case 'completed':
      return { label: 'Completed', className: 'bg-emerald-100 text-emerald-700 border-transparent' };
    case 'in_progress':
      return { label: 'In Progress', className: 'bg-blue-100 text-blue-700 border-transparent' };
    case 'scheduled':
      return { label: 'Scheduled', className: 'bg-amber-100 text-amber-700 border-transparent' };
    case 'pending':
      return { label: 'Pending', className: 'bg-gray-100 text-gray-700 border-transparent' };
    case 'pending_calibration':
      return { label: 'Pending Calibration', className: 'bg-purple-100 text-purple-700 border-transparent' };
    default:
      return {
        label: status.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        className: 'bg-gray-100 text-gray-700 border-transparent',
      };
  }
};

// The review-side vocabulary (underscored), which is what this endpoint takes.
const RATING_OPTIONS: { value: string; label: string }[] = [
  { value: 'exceptional', label: 'Exceptional' },
  { value: 'full', label: 'Full' },
  { value: 'not_yet_full', label: 'Not Yet Full' },
  { value: 'weak', label: 'Weak' },
  { value: 'tstr', label: 'Too Soon To Rate' },
];

const ratingLabel = (v?: string | null): string => {
  if (!v) return 'Not rated';
  const key = String(v).toLowerCase().replace(/-/g, '_');
  return RATING_OPTIONS.find((o) => o.value === key)?.label ?? String(v);
};

const ratingClass = (v?: string | null): string => {
  switch (String(v || '').toLowerCase().replace(/-/g, '_')) {
    case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'full': return 'bg-green-100 text-green-800 border-green-200';
    case 'not_yet_full': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'weak': return 'bg-red-100 text-red-800 border-red-200';
    case 'tstr': return 'bg-slate-100 text-slate-700 border-slate-300';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

/** Formal reviews are the only ones that carry an overall rating. */
const isFormalReview = (t: string): boolean =>
  ['midyear', 'mid-year', 'yearend', 'year-end'].includes(String(t).toLowerCase());

const ReviewReopenAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [year, setYear] = useState<number>(currentYear);
  const [confirmSession, setConfirmSession] = useState<ReviewSessionData | null>(null);

  // Rating correction
  const [ratingTarget, setRatingTarget] = useState<ReviewSessionData | null>(null);
  const [newRating, setNewRating] = useState<string>('');
  const [ratingReason, setRatingReason] = useState('');
  const [ratingError, setRatingError] = useState<string | null>(null);

  const employeeId = employee?.id;

  const {
    data: sessions,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-user-sessions', employeeId, year],
    queryFn: () => reviewService.getUserReviewCycle(employeeId as string, year, true),
    enabled: !!employeeId,
  });

  const reopenMutation = useMutation({
    mutationFn: (sessionId: string) => reviewService.reopenReview(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-sessions', employeeId, year] });
      toast.success('Review reopened');
      setConfirmSession(null);
    },
    onError: (err: any) => {
      console.error('Error reopening review:', err);
      toast.error(err?.detail || err?.response?.data?.detail || err?.message || 'Failed to reopen review');
      setConfirmSession(null);
    },
  });

  // Every correction for this person, so the log is visible where the change is
  // made rather than on another tab.
  const { data: changeLog } = useQuery({
    queryKey: ['rating-corrections', employeeId],
    queryFn: () => calibrationRemediationService.listRemediations({ employeeId, action: 'rating', limit: 20 }),
    enabled: !!employeeId,
  });

  const currentRatingOf = (s: ReviewSessionData): string | null | undefined =>
    s.calibrated_rating ?? s.initial_rating;

  const closeRatingDialog = () => {
    setRatingTarget(null); setNewRating(''); setRatingReason(''); setRatingError(null);
  };

  const ratingMutation = useMutation({
    mutationFn: ({ sessionId, rating, reason }: { sessionId: string; rating: string; reason: string }) =>
      reviewService.correctReviewRating(sessionId, rating, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-sessions', employeeId, year] });
      queryClient.invalidateQueries({ queryKey: ['rating-corrections', employeeId] });
      toast.success(res.message);
      closeRatingDialog();
    },
    // The server's refusals are the whole point here — show them verbatim.
    onError: (err: any) => setRatingError(err?.detail || err?.message || 'Failed to change the rating'),
  });

  // Single-select: take the most recently selected employee so re-picking swaps.
  const handleSelectionChange = (employees: PickedEmployee[]) => {
    setEmployee(employees.length > 0 ? employees[employees.length - 1] : null);
  };

  const yearOptions = [currentYear, currentYear - 1];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <RotateCcw className="w-6 h-6 text-heritage-blue" />
          Reopen a Review
        </h2>
        <p className="text-gray-600 mt-1">
          Return a completed check-in to In-Progress so stranded items can be added and
          the People Leader can re-finalise it.
        </p>
      </div>

      {/* Helper caption */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-heritage-blue flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            Reopening a review reverts it from <span className="font-medium">Completed</span> to{' '}
            <span className="font-medium">In-Progress</span>. The frozen snapshot is cleared so the
            reopened review reflects live objectives and measurements, and the People Leader must
            re-finalise it. Only completed reviews can be reopened — and not once calibration has
            started or concluded (those are locked to protect the calibrated rating).
          </p>
        </div>
      </Card>

      {/* Selectors */}
      <Card className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Member</label>
            <EmployeePicker
              selectedEmployees={employee ? [employee] : []}
              onSelectionChange={handleSelectionChange}
              placeholder="Search a team member by name, role, or function..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results */}
      {!employeeId ? (
        <Card className="p-10">
          <div className="text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Select a team member</p>
            <p className="text-sm mt-2 max-w-md mx-auto">
              Search for a team member above to view their review sessions for {year}.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Review sessions{employee ? ` — ${employee.name}` : ''} ({year})
            </h3>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-heritage-blue" />
            </div>
          ) : isError ? (
            <div className="text-center text-red-600 py-10">
              <p>Failed to load review sessions.</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          ) : !sessions || sessions.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              <p className="text-base font-medium">No review sessions for this employee/year</p>
              <p className="text-sm mt-1">Try a different year, or confirm the team member has a review cycle.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Review type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => {
                  const badge = getStatusBadge(session.status);
                  const isCompleted = session.status === 'completed';
                  // Calibration lock: a review whose calibration has started or
                  // concluded can't be reopened (server enforces this too). Also
                  // treat an already-calibrated rating as locked (belt & braces).
                  const calLocked =
                    isCompleted && (!!session.calibration_locked || !!session.calibrated_rating);
                  const isThisReopening =
                    reopenMutation.isPending && reopenMutation.variables === session.id;
                  return (
                    <TableRow key={session.id}>
                      <TableCell className="font-medium text-gray-900">
                        {formatReviewType(session.review_type)}
                      </TableCell>
                      <TableCell>
                        <Badge className={badge.className}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {isFormalReview(session.review_type) ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={ratingClass(currentRatingOf(session))}>
                              {ratingLabel(currentRatingOf(session))}
                            </Badge>
                            {!!session.calibrated_rating && (
                              <span className="text-xs text-gray-500">calibrated</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                        {/* Rating correction is available on any formal review
                            that has a rating — including the calibrated ones
                            that reopening deliberately refuses, which is the
                            case HR actually keeps hitting. */}
                        {isFormalReview(session.review_type) && !!currentRatingOf(session) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setRatingTarget(session);
                              setNewRating('');
                              setRatingReason('');
                              setRatingError(null);
                            }}
                          >
                            <SlidersHorizontal className="w-4 h-4 mr-2" />
                            Change rating
                          </Button>
                        )}
                        {!isCompleted ? (
                          <span className="text-gray-400">—</span>
                        ) : calLocked ? (
                          <span
                            className="inline-flex items-center gap-1.5 text-xs text-gray-500"
                            title="Reopening is blocked to protect the calibrated rating and avoid stranding the calibration session."
                          >
                            <Lock className="w-3.5 h-3.5" />
                            Calibration {formatCalStatus(session.calibration_status)}
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmSession(session)}
                            disabled={reopenMutation.isPending}
                          >
                            {isThisReopening ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Reopening...
                              </>
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reopen
                              </>
                            )}
                          </Button>
                        )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Recent rating changes for this person */}
      {!!employeeId && !!changeLog?.rows.length && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <History className="w-5 h-5 text-heritage-blue" />
            Rating changes
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Every correction made here, with the reason given at the time.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Change</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {changeLog.rows.map((r) => {
                return (
                  <TableRow key={r.id} className={r.is_undone ? 'text-gray-500' : undefined}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={ratingClass(r.rating_before)}>
                          {ratingLabel(r.rating_before)}
                        </Badge>
                        <span className="text-gray-400">&rarr;</span>
                        <Badge variant="outline" className={ratingClass(r.rating_after)}>
                          {ratingLabel(r.rating_after)}
                        </Badge>
                        {r.is_undone && <span className="text-xs text-gray-500">undone</span>}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-sm text-sm text-gray-700">{r.reason}</TableCell>
                    <TableCell className="text-sm text-gray-700">
                      <div>{r.applied_by}</div>
                      <div className="text-xs text-gray-500">
                        {r.applied_at ? new Date(r.applied_at).toLocaleDateString('en-ZA', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        }) : ''}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Change rating dialog */}
      <Dialog
        open={!!ratingTarget}
        onOpenChange={(open) => { if (!open && !ratingMutation.isPending) closeRatingDialog(); }}
      >
        <DialogContent>
          {ratingTarget && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Change rating — {employee?.name ?? 'team member'}
                </DialogTitle>
                <DialogDescription>
                  {formatReviewType(ratingTarget.review_type)}
                  {ratingTarget.calibrated_rating
                    ? ' — this rating was set in calibration, so the calibrated value is what changes.'
                    : ' — this is the People Leader\u2019s assessment on the check-in.'}
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <span className="text-sm text-gray-600">Currently</span>
                <Badge variant="outline" className={ratingClass(currentRatingOf(ratingTarget))}>
                  {ratingLabel(currentRatingOf(ratingTarget))}
                </Badge>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="new-rating" className="text-sm">New rating</Label>
                <Select value={newRating} onValueChange={setNewRating}>
                  <SelectTrigger id="new-rating">
                    <SelectValue placeholder="Choose a rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {RATING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rating-reason" className="text-sm">
                  Reason <span className="text-gray-500">(recorded against the change)</span>
                </Label>
                <Textarea
                  id="rating-reason"
                  rows={2}
                  value={ratingReason}
                  onChange={(e) => setRatingReason(e.target.value)}
                  placeholder="e.g. Moderation outcome agreed with the One-Up on 12 Aug."
                />
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  The new rating is visible to the employee straight away, and their rating
                  history is updated to match.
                </p>
              </div>

              {ratingError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {ratingError}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={closeRatingDialog} disabled={ratingMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  onClick={() => ratingMutation.mutate({
                    sessionId: ratingTarget.id, rating: newRating, reason: ratingReason,
                  })}
                  disabled={ratingMutation.isPending || !newRating || ratingReason.trim().length < 5}
                >
                  {ratingMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Change rating
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <AlertDialog
        open={!!confirmSession}
        onOpenChange={(open) => {
          if (!open && !reopenMutation.isPending) setConfirmSession(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this review?</AlertDialogTitle>
            <AlertDialogDescription>
              Reopen this completed{' '}
              {confirmSession ? formatReviewType(confirmSession.review_type) : 'review'}
              {employee ? ` for ${employee.name}` : ''}? It returns to In-Progress; the People
              Leader must re-finalise it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopenMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirmSession) reopenMutation.mutate(confirmSession.id);
              }}
              disabled={reopenMutation.isPending}
            >
              {reopenMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reopening...
                </>
              ) : (
                'Reopen review'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewReopenAdmin;
